import { useCallback } from 'react';

import { useShallow } from 'zustand/react/shallow';

import SocketService from '@src/services/SocketService';
import { useMagicPenStore, useRoomStore } from '@src/stores';

import { EMagicPenSockets } from '@src/types';

const useMagicPenActions = () => {
  const { setSaveModalState } = useMagicPenStore(
    useShallow((state) => ({
      setSaveModalState: state.setSaveModalState,
    }))
  );

  const { setIsMagicPenVisible } = useRoomStore(
    useShallow((state) => ({
      setIsMagicPenVisible: state.setIsMagicPenVisible,
    }))
  );

  const handleStopAnnotating = useCallback(async () => {
    try {
      const response = await SocketService.request(EMagicPenSockets.HIDE_MAGIC_PEN);
      if (response.success && response.deletedAnnotationsIds) {
        const annotationsIdsToHide = response.deletedAnnotationsIds;
        if (!annotationsIdsToHide) return;

        useMagicPenStore.setState((state) => ({
          objects: state.objects.filter((obj) => !annotationsIdsToHide.includes(obj.id)),
        }));
      }
      // Handle revoked peers annotate permission if needed
      if (response.revokedPeersAnnotatePermission) {
        // Update store state to reflect revoked permissions
        useRoomStore.getState().setHasMagicPenAnnotatePermission(false);
      }
    } catch (err) {
      console.error('Hide Magic Pen error:', err);
    }

    setSaveModalState({ isSaveModalOpen: false });
    setIsMagicPenVisible(false);
  }, [setIsMagicPenVisible, setSaveModalState]);

  // handle hiding magic pen with conditional confirmation
  const handleHideMagicPen = useCallback(
    async (objectsLength: number) => {
      if (objectsLength > 0) {
        // If there are objects, show confirmation modal
        setSaveModalState({
          isSaveModalOpen: true,
          saveModalTriggerSource: 'popper',
        });
      } else {
        // If there are no objects, directly hide the magic pen
        await handleStopAnnotating();
      }
    },
    [handleStopAnnotating, setSaveModalState]
  );

  return {
    handleHideMagicPen,
    handleStopAnnotating,
  };
};

export default useMagicPenActions;
